"use client";

import { BarChart3 } from "lucide-react";
import { makeFunctionReference } from "convex/server";
import { useQuery } from "convex/react";
import { CarrierLanePreferencesPanel } from "@/components/carrier/CarrierLanePreferencesPanel";
import { CustomerPerformanceReportPanel } from "@/components/scorecards/CustomerPerformanceReportPanel";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { QuerySkeleton } from "@/components/ui/skeleton";
import type { EntityScorecard } from "@/core/console/scorecards";

interface ScorecardResult {
  entityName: string;
  scorecard: EntityScorecard;
}

const customerScorecard = makeFunctionReference<
  "query",
  { tenantId: string; customerId?: string; customerName?: string },
  ScorecardResult
>("scorecards:customer");
const carrierScorecard = makeFunctionReference<
  "query",
  { tenantId: string; carrierMc: string },
  ScorecardResult
>("scorecards:carrier");

export function EntityScorecardView({
  tenantId,
  kind,
  entityId,
}: {
  tenantId: string;
  kind: "customer" | "carrier";
  entityId: string;
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <ScorecardLayout result={demoScorecard(entityId)} kind={kind} tenantId={tenantId} entityId={entityId} />;
  }
  return <LiveScorecard tenantId={tenantId} kind={kind} entityId={entityId} />;
}

function LiveScorecard(props: { tenantId: string; kind: "customer" | "carrier"; entityId: string }) {
  const result = useQuery(
    props.kind === "customer" ? customerScorecard : carrierScorecard,
    props.kind === "customer"
      ? { tenantId: props.tenantId, customerId: props.entityId }
      : { tenantId: props.tenantId, carrierMc: props.entityId },
  );
  if (result === undefined) {
    return (
      <main className="mx-auto max-w-5xl px-5 py-8">
        <QuerySkeleton surface="entityScorecard" />
      </main>
    );
  }
  return <ScorecardLayout result={result} kind={props.kind} tenantId={props.tenantId} entityId={props.entityId} />;
}

function ScorecardLayout({
  result,
  kind,
  tenantId,
  entityId,
}: {
  result: ScorecardResult;
  kind: "customer" | "carrier";
  tenantId: string;
  entityId: string;
}) {
  const metrics = result.scorecard;
  return (
    <main className="mx-auto max-w-5xl px-5 py-8">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{result.entityName}</h1>
          <p className="text-sm text-muted-foreground">{kind === "customer" ? "Customer" : "Carrier"} performance scorecard.</p>
        </div>
        <Badge variant="info">{metrics.loadCount} loads</Badge>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 size={16} />
            Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-5">
          <Metric label="On-time" value={`${metrics.onTimePct}%`} />
          <Metric label="Claim rate" value={`${metrics.claimRatePct}%`} />
          <Metric label="Avg response" value={metrics.avgResponseMinutes === null ? "n/a" : `${metrics.avgResponseMinutes}m`} />
          <Metric label="Open exceptions" value={String(metrics.openExceptionCount)} />
          <Metric label="Loads" value={String(metrics.loadCount)} />
        </CardContent>
      </Card>
      {kind === "customer" ? (
        <CustomerPerformanceReportPanel tenantId={tenantId} customerId={entityId} customerName={result.entityName} />
      ) : null}
      {kind === "carrier" ? <CarrierLanePreferencesPanel tenantId={tenantId} carrierMc={entityId} /> : null}
    </main>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function demoScorecard(entityName: string): ScorecardResult {
  return {
    entityName,
    scorecard: {
      loadCount: 24,
      onTimePct: 91.7,
      claimRatePct: 4.2,
      avgResponseMinutes: 34,
      openExceptionCount: 2,
    },
  };
}
